import React, { useEffect, useState } from "react";
import {
  Route,
  Switch,
  Redirect,
  withRouter,
} from "react-router-dom";
import classnames from "classnames";
import { Box, CircularProgress } from "@material-ui/core";

// styles
import useStyles from "./styles";

// components
import Header from "../Header";
import Sidebar from "../Sidebar";

// pages
import Manage from "../../pages/tables"
import Summary from "../../pages/Summary/Summary"
import CeleryLogsTable from "../../pages/Summary/CeleryLogsTable"
import Chat from "../../pages/Chat/Chat";
import NotificationList from "../../pages/NotificationList"
import AccountPage from "../../pages/ManageUsers"
import Reindex from "../../Reindex/Reindex";
import ManageVideoSummaries from "../../Reindex/ManageVideoSummaries";
import Upload from '../../pages/uploadFile/UploadTable'
import Mapping from "../../Mapping_Towns/Mapping";
import StatsPage from "../../pages/StatsPage";
import YoutubeAPIKeyManager from "../../pages/MangeAPI"
// contexts
import { useLayoutState } from "../../context/LayoutContext";
import State from "../../pages/States/States";
import Dictionary from "../../pages/Dictionary/Dictionary";
import ReportedIssuesPage from "../../pages/ReportedIssues";
import FeedbacksPage from "../../pages/FeedbacksPage";
import Plan from "../../pages/Plans/Plan";
import { BACKEND_BASE_URL } from "../../config";
import CityAgreement from "../../pages/CityAgreement/CityAgreement";
import api from "../api";
import ManageAwareCapture from "../../pages/ManageAwareCapture.jsx";
import DemoAccessPage from "../../pages/DemoAccessPage"; // adjust path if you put the file elsewhere

import ManageMeeting from "../../pages/ManageMeeting.jsx";
import MeetingTags from "../../pages/MeetingTags.jsx";
import CaptureEvents from "../../pages/CaptureEvents.jsx";
import axios from "axios";
// import VideoDialog from "../../pages/Summary/VedioDialog"

const STATS_CACHE_TTL_MS = 15 * 60 * 1000;
const STATS_PREFETCH_LOCK_MS = 90 * 1000;
const STATS_PREFETCH_PAGE1_KEY = "admin_stats_page_1_cache_v1";
const CONTENT_ERRORS_CACHE_KEY = "admin_content_errors_cache_v1";
const PREFETCH_LOCK_KEY = "admin_stats_prefetch_lock_v1";

function getCachePayload(storageKey) {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.fetchedAt !== "number") return null;
    if (Date.now() - parsed.fetchedAt > STATS_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function Layout(props) {
  var classes = useStyles();

  // global
  var layoutState = useLayoutState();
  const [user, setUser] = useState();

  useEffect(() => {
    fetchCurrentUser();
  }, []);

const fetchCurrentUser = async () => {
  try {
    const token = localStorage.getItem("authToken");

    if (!token) {
      console.warn("🚫 No token found in localStorage.");
      setUser(null);
      return;
    }

    console.log("📡 Fetching current user with token:", token);

    const response = await api.get(
      `${BACKEND_BASE_URL}/api/admin-user/current_user/`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log("✅ Fetched current user:", response.data);
    setUser(response.data);

  } catch (error) {
    console.error("❌ Failed to fetch current user:", error);

    if (error.response?.status === 401 || error.response?.status === 403) {
      console.warn("🔒 Unauthorized - token may be invalid or expired.");
      // Optional: Redirect or logout
      // navigate("/login");
    }
  }
};



  // Show app shell immediately; only content area shows loading until user is known
  const userReady = user !== undefined;
  const userRole = user?.role || null;
  const isAdmin = user?.is_staff || user?.is_superuser || false;
  const isDemoUser = userRole === "DEMO";
  const isBulkImportUser = userRole === "BULK_IMPORT";

  useEffect(() => {
    if (!userReady || !isAdmin) return undefined;

    const hasStatsCache = !!getCachePayload(STATS_PREFETCH_PAGE1_KEY);
    const hasContentCache = !!getCachePayload(CONTENT_ERRORS_CACHE_KEY);
    if (hasStatsCache && hasContentCache) return undefined;

    const now = Date.now();
    try {
      const lockRaw = sessionStorage.getItem(PREFETCH_LOCK_KEY);
      if (lockRaw) {
        const lockTs = Number(lockRaw);
        if (Number.isFinite(lockTs) && now - lockTs < STATS_PREFETCH_LOCK_MS) {
          return undefined;
        }
      }
      sessionStorage.setItem(PREFETCH_LOCK_KEY, String(now));
    } catch {
      return undefined;
    }

    const timerId = window.setTimeout(async () => {
      try {
        const [statsRes, contentRes] = await Promise.allSettled([
          axios.get(`${BACKEND_BASE_URL}/api/municipality/stats/?page=1`),
          axios.get(`${BACKEND_BASE_URL}/api/content-error/`),
        ]);

        if (statsRes.status === "fulfilled") {
          sessionStorage.setItem(
            STATS_PREFETCH_PAGE1_KEY,
            JSON.stringify({ fetchedAt: Date.now(), data: statsRes.value.data }),
          );
        }

        if (contentRes.status === "fulfilled") {
          sessionStorage.setItem(
            CONTENT_ERRORS_CACHE_KEY,
            JSON.stringify({ fetchedAt: Date.now(), data: contentRes.value.data }),
          );
        }
      } finally {
        sessionStorage.removeItem(PREFETCH_LOCK_KEY);
      }
    }, 2500);

    return () => window.clearTimeout(timerId);
  }, [userReady, isAdmin]);

  return (
    <div className={classes.root}>
      <>
        <Header history={props.history} />
        <Sidebar user={user} />
        <div
          className={classnames(classes.content, {
            [classes.contentShift]: layoutState.isSidebarOpened,
          })}
        >
          <div className={classes.fakeToolbar} />
          {!userReady ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <CircularProgress />
            </Box>
          ) : (
          <Switch>
          {isDemoUser ? (
              // DEMO users only have access to demo-access page
              <>
                <Route path="/app/demo-access" component={DemoAccessPage} />
                <Redirect from="*" to="/app/demo-access" />
              </>
            ) : isBulkImportUser ? (
              // BULK_IMPORT users only have access to upload page
              <>
                <Route path="/app/upload" component={Upload} />
                <Redirect from="*" to="/app/upload" />
              </>
            ) : isAdmin ? (
              // ADMIN users have access to all pages
              <>
                <Route path="/app/muncipality" component={State} />
                <Route path="/app/demo-access" component={DemoAccessPage} />
                <Route path="/app/manage" component={Manage} />
                <Route path="/app/upload" component={Upload} />
                <Route path="/app/summary" component={Summary} />
                <Route path="/app/transcript-logs" component={CeleryLogsTable} />
                <Route path="/app/dictionary" component={Dictionary} />
                <Route path="/app/chat" component={Chat} />
                <Route path="/app/users" component={AccountPage} />
                <Route path="/app/notifications" component={NotificationList} />
                <Route path="/app/mapping" component={Mapping} />
                <Route path="/app/reindex" component={Reindex} />
                <Route path="/app/video-summary-recovery" component={ManageVideoSummaries} />
                <Route path="/app/stats" component={StatsPage} />
                <Route path="/app/manage-api/" component={YoutubeAPIKeyManager}/>
                <Route path="/app/reported-issues/" component={ReportedIssuesPage}/>
                <Route path="/app/feedbacks/" component={FeedbacksPage}/>
                <Route path="/app/plans/" component={Plan}/>
                <Route path="/app/city-agreement/" component={CityAgreement}/>
               

                <Route path="/app/manage-meeting" component={ManageMeeting} />
                <Route path="/app/meeting-tags" component={MeetingTags} />
                <Route path="/app/capture-events" component={CaptureEvents} />
                <Route path="/app/aware-capture" component={ManageAwareCapture} />
                {/* <Redirect from="*" to="/app/manage" /> */}
              </>
            ) : (
              // Fallback: redirect to login if no valid role
              <Redirect from="*" to="/login" />
            )}
          </Switch>
          )}
        </div>
      </>
    </div>
  );
}

export default withRouter(Layout);
